import { z } from 'zod';
import { MAX_USER_SOURCE_URL_CHARS } from './constants.js';

/**
 * `userSourceUrlSchema` — the constrained https URL of the USER-SOURCE tile class
 * (D-017 / ADR-005 §2). This is the ONE place ADR-002's "no URLs in configs" rule
 * is amended: a user-source tile's schema MAY carry a URL, but ONLY under these
 * constraints, and ONLY because the URL is fetched CLIENT-SIDE (the user's own
 * browser), NEVER by our Lambda. The arbitrary-URL server proxy stays OUT until a
 * hardened fetcher exists (ADR-005 §1, Phase 4).
 *
 * This validator is a SECURITY CONTROL, not a convenience: the URL is untrusted
 * (hand-editable, cloneable). The constraints are a BEST-EFFORT private-address
 * hygiene blocklist — they raise the bar against a config that points a browser at
 * localhost / the LAN / cloud metadata, but they are explicitly NOT
 * DNS-rebinding-proof (a public hostname can still resolve to a private address at
 * fetch time). Residual risk is stated in ADR-005 §"Residual risk": a client-side
 * fetch of a user URL is ≈ the risk of the user clicking any hyperlink.
 *
 * CONSTRAINTS (all enforced below; hostile tests in user-source-url.test.ts):
 *   1. https ONLY. Scheme must be exactly `https:` (the WHATWG parser lowercases it,
 *      so mixed-case `HtTpS://` is accepted-and-normalized as https; every other
 *      scheme — http, ftp, file, javascript, data, ws — is rejected).
 *   2. NO credentials. `user:pass@host` / `user@host` — a non-empty username or
 *      password is rejected (credential-bearing URLs are a phishing/exfil vector).
 *   3. NO localhost family. hostname === 'localhost', or ends with '.localhost',
 *      '.local', or '.internal' — rejected (loopback + mDNS + private-suffix).
 *   4. NO private/loopback/link-local IPv4 literals (pragmatic ranges; see below).
 *   5. NO IPv6 literals AT ALL. Any bracketed host (`[...]`) is rejected outright —
 *      we do not attempt per-range v6 classification (documented simplification;
 *      the loss is: a user cannot paste a raw-IPv6 feed URL, which is vanishingly
 *      rare and always expressible via a hostname).
 *   6. DEFAULT PORT ONLY. An explicit port is rejected — even `:443`. Simplest safe
 *      rule (documented): a non-default port is the tell of an internal service
 *      (`:8080`, `:9200`, `:2375`); real public feeds serve on 443. The WHATWG
 *      parser drops a redundant `:443` (it equals the https default) so a literal
 *      `:443` is INDISTINGUISHABLE from no port and is therefore accepted — this is
 *      the one documented seam in "default port only".
 *   7. LENGTH ≤ 300 chars (MAX_USER_SOURCE_URL_CHARS). Bounds the shared-board blob.
 *   8. NO whitespace anywhere (leading/trailing/interior) — a whitespace char is a
 *      classic parser-confusion vector; reject before parsing.
 *
 * ACCEPTED RESIDUAL (documented, ADR-005 §2): a punycode/Unicode hostname that
 * merely LOOKS like localhost (e.g. `xn--` homographs, `1ocalhost`, `localhost.example.com`)
 * is accepted — it is a normal public hostname; blocking look-alikes is a
 * bottomless rabbit hole and the client-side-only fetch model already bounds the
 * blast radius. We block the EXACT localhost family (constraint 3), not look-alikes.
 */

/**
 * Pragmatic IPv4-literal detector for the private/loopback/link-local ranges we
 * refuse. Deliberately a focused regex, NOT a full IPv4 grammar: the WHATWG URL
 * parser has already normalized the host, so we only need to catch a
 * dotted-quad that lands in a blocked range. Ranges blocked:
 *   - 10.0.0.0/8         (private)
 *   - 127.0.0.0/8        (loopback — 127.0.0.1 and friends)
 *   - 169.254.0.0/16     (link-local — includes 169.254.169.254 cloud metadata)
 *   - 172.16.0.0/12      (private — 172.16 … 172.31)
 *   - 192.168.0.0/16     (private)
 *   - 0.0.0.0/8          (this-network / unspecified; 0.x is often a loopback alias)
 * A public dotted-quad (e.g. 8.8.8.8) does NOT match and is allowed (it is no
 * different from a public hostname — the client fetches it directly).
 */
const BLOCKED_IPV4 =
  /^(?:10|127|0)\.(?:\d{1,3})\.(?:\d{1,3})\.(?:\d{1,3})$|^169\.254\.(?:\d{1,3})\.(?:\d{1,3})$|^192\.168\.(?:\d{1,3})\.(?:\d{1,3})$|^172\.(?:1[6-9]|2\d|3[01])\.(?:\d{1,3})\.(?:\d{1,3})$/;

/** True if the (already URL-normalized) hostname is in the localhost family (#3). */
function isLocalhostFamily(hostname: string): boolean {
  // Strip a single FQDN root dot first (review HIGH-2): the WHATWG parser
  // preserves it for named hosts, so `localhost.` — which resolves to loopback
  // on virtually every resolver — would otherwise slip both the exact match
  // and the suffix checks. (IPv4 literals get the dot stripped by the parser
  // itself, so the IP blocklist never had this gap.)
  const h = hostname.toLowerCase().replace(/\.$/, '');
  return (
    h === 'localhost' ||
    h.endsWith('.localhost') ||
    h.endsWith('.local') ||
    h.endsWith('.internal')
  );
}

/**
 * The pure predicate behind the schema. Returns true only for a URL string that
 * satisfies every constraint. Total and side-effect-free: any parse failure or
 * violated constraint yields `false` (fail-closed).
 */
export function isSafeUserSourceUrl(raw: string): boolean {
  // #8 no whitespace (pre-parse: a whitespace char is a parser-confusion vector).
  if (/\s/.test(raw)) return false;
  // #7 length cap (pre-parse: bound work + the serialized blob).
  if (raw.length > MAX_USER_SOURCE_URL_CHARS) return false;
  // #5 reject ALL IPv6 literals outright — any bracketed host.
  if (raw.includes('[') || raw.includes(']')) return false;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false; // unparseable → reject.
  }

  // #1 https ONLY (scheme is lowercased by the parser).
  if (url.protocol !== 'https:') return false;
  // #2 NO credentials.
  if (url.username !== '' || url.password !== '') return false;
  // #6 DEFAULT PORT ONLY (the parser blanks a redundant :443; any other port stays).
  if (url.port !== '') return false;

  const hostname = url.hostname;
  if (hostname === '') return false; // defensive: https requires a host anyway.
  // #3 localhost family.
  if (isLocalhostFamily(hostname)) return false;
  // #4 private/loopback/link-local IPv4 literal.
  if (BLOCKED_IPV4.test(hostname)) return false;

  return true;
}

/**
 * The Zod schema. A plain string, length-capped at the Zod layer too (so an
 * over-long value gets a clean length error before the refinement runs), then
 * refined by `isSafeUserSourceUrl`. `.strict()` object rules on the enclosing
 * params schema still bite; this is the value-level guard.
 */
export const userSourceUrlSchema = z
  .string()
  .max(MAX_USER_SOURCE_URL_CHARS)
  .refine(isSafeUserSourceUrl, {
    message:
      'must be a plain https:// URL (no credentials, no port, no private/loopback host)',
  });

export type UserSourceUrl = z.infer<typeof userSourceUrlSchema>;
